import { deploymentSetting } from '../runtime-config.js';

/**
 * Trusted portal origin allow-list (CSAGE-314).
 *
 * Mirrors the `frame-ancestors` directive in apps/web/nginx.conf:44 — every
 * origin nginx allows to FRAME Sage is also an origin the SPA will accept a
 * postMessage FROM. Keeping these in lockstep matters: `frame-ancestors` is
 * enforced by the browser at the framing layer, but nothing enforced the
 * matching trust at the postMessage layer before this change — an attacker
 * who could not frame Sage could still `window.open` it and post a forged
 * session. See openspec/changes/harden-portal-postmessage-origin/design.md
 * (D2, D3) and portal-origins.nginx.test.ts, which fails the build if this
 * module and nginx.conf ever diverge.
 *
 * Do NOT edit DEFAULT_PORTAL_ORIGIN_PATTERNS or DEV_ONLY_PORTAL_ORIGIN_PATTERNS
 * without also updating apps/web/nginx.conf:44 (and vice versa).
 */

interface ParsedPattern {
  readonly scheme: 'http:' | 'https:';
  readonly wildcard: boolean;
  readonly host: string;
  readonly port: string;
}

// Mirrors apps/web/nginx.conf:44, minus 'self' (→ window.location.origin,
// always trusted — see isAllowedPortalOrigin) and minus the two localhost
// entries (→ DEV_ONLY_PORTAL_ORIGIN_PATTERNS, dev builds only).
export const DEFAULT_PORTAL_ORIGIN_PATTERNS: readonly string[] = [
  'https://*.my-canopy.com',
  'https://*.iot.ricoh-usa.com',
  // CSAGE-373: KIS white-label portals, prod + UAT. Exact hosts, not
  // `*.kiosk.com` — see the trust-boundary note in nginx.conf.
  'https://portal.kiosk.com',
  'https://uat.kiosk.com',
  'https://sage.gocanopy.io',
];

// The two nginx.conf:44 localhost entries. Trusted by the SPA in dev
// builds only — strictly tighter than nginx, which lists them
// unconditionally. See design.md D3 "Why localhost is dev-only".
export const DEV_ONLY_PORTAL_ORIGIN_PATTERNS: readonly string[] = [
  'http://localhost:3000',
  'http://localhost:4200',
];

// Pattern grammar: scheme://[*.]host[:port], scheme http|https. Parsed with
// an explicit regex — NOT `new URL()` — per design D2: relying on `new
// URL('https://*.my-canopy.com')` happening to succeed is relying on an
// accident of the WHATWG host-parsing algorithm, not a documented contract.
const PATTERN_GRAMMAR = /^(https?):\/\/(\*\.)?([a-z0-9.-]+)(?::(\d+))?$/iu;

function parsePattern(raw: string): ParsedPattern {
  const match = PATTERN_GRAMMAR.exec(raw.trim());
  if (!match) {
    throw new Error(
      `portal-origins: malformed allow-list entry "${raw}" — expected scheme://[*.]host[:port] with scheme http or https`,
    );
  }
  const [, scheme, wildcardMark, host, port] = match;
  return {
    scheme: `${scheme!.toLowerCase()}:` as 'http:' | 'https:',
    wildcard: wildcardMark === '*.',
    host: host!.toLowerCase(),
    port: port ?? '',
  };
}

// Defensive cast so this module is importable under plain Node (tests,
// SSR-less builds) — matches the pattern already used at
// apps/web/src/api/api-origin.ts:27-28.
//
// process.env is checked first: it's what vi.stubEnv() (used by the test
// suite to exercise DEV-gating and VITE_PORTAL_ORIGINS) actually mutates.
// Vite's import.meta.env is a snapshot taken once when the dev/test server
// boots, so under vitest it does not reflect an env change made after
// startup. In a real browser build there is no `process` global — Vite
// never polyfills one for client code — so this branch is unreachable
// outside Node, and import.meta.env is used. The portal-origin override itself
// comes from runtime-config.ts (CSAGE-437): in the container image, the same
// SAGE_PORTAL_ORIGINS value that sets nginx's frame-ancestors.
function readEnvValue(key: string): string | boolean | undefined {
  if (typeof process !== 'undefined' && process.env && key in process.env) {
    return process.env[key];
  }
  return (import.meta as unknown as { env?: Record<string, string | boolean> }).env?.[key];
}

// Exported so portal-message.ts's dev-only rejection warning (design R4)
// uses the same dev-build determination as the allow-list itself, rather
// than a second copy of the process.env/import.meta.env dance.
export function isDevBuild(): boolean {
  return Boolean(readEnvValue('DEV'));
}

function resolvePatternStrings(): string[] {
  // CSAGE-437: the container's SAGE_PORTAL_ORIGINS, through /runtime-config.js,
  // or VITE_PORTAL_ORIGINS under vite dev and tests.
  const override = deploymentSetting('portalOrigins');
  const base =
    typeof override === 'string' && override.trim()
      ? override
          .split(',')
          .map((entry) => entry.trim())
          .filter(Boolean)
      : [...DEFAULT_PORTAL_ORIGIN_PATTERNS];
  return isDevBuild() ? [...base, ...DEV_ONLY_PORTAL_ORIGIN_PATTERNS] : base;
}

// Parsed once at module load: a malformed entry is a hard error at boot,
// never a silently skipped allow-list entry (design D2, spec "Malformed
// allow-list entry is rejected at load").
const ALLOWED_PATTERNS: readonly ParsedPattern[] = resolvePatternStrings().map(parsePattern);

function selfOrigin(): string | null {
  return typeof window !== 'undefined' && window.location ? window.location.origin : null;
}

/**
 * Structural origin matcher (design D2). Parses `origin` as a URL and
 * compares scheme, host, and port against the allow-list — never raw-string
 * containment. Wildcard entries match by leading-dot suffix on the parsed
 * hostname, so `evil-my-canopy.com` and the bare apex `my-canopy.com` are
 * both rejected against `*.my-canopy.com`.
 *
 * FORBIDDEN in this function: `endsWith` / `includes` / `indexOf` against
 * the raw origin string, or a regex built by string-concatenating a
 * pattern without escaping `.`. Every comparison below is against
 * `URL`-parsed components only.
 */
export function isAllowedPortalOrigin(origin: unknown): boolean {
  if (typeof origin !== 'string' || origin === '' || origin === 'null') return false;

  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return false;
  }

  // The SPA's own origin (CSP's 'self') is always trusted, even if it
  // appears in no configured pattern.
  const self = selfOrigin();
  if (self !== null && url.origin === self) return true;

  const hostname = url.hostname.toLowerCase();
  const port = url.port; // '' for the scheme's default port — URL normalizes this.

  for (const pattern of ALLOWED_PATTERNS) {
    if (url.protocol !== pattern.scheme) continue;
    if (port !== pattern.port) continue;
    if (pattern.wildcard) {
      if (hostname.endsWith(`.${pattern.host}`) && hostname.length > pattern.host.length + 1) {
        return true;
      }
    } else if (hostname === pattern.host) {
      return true;
    }
  }
  return false;
}
